import { useState, useEffect } from 'react'
import type { DrinkEntry } from '@/types'
import Modal from '@/components/layout/Modal'
import { formatDateTimeLocal, formatDateForStorage } from '@/utils/time'
import moment from 'moment'

interface GapFormProps {
  isOpen: boolean
  onClose: () => void
  onSave: (entry: DrinkEntry) => void
  lastDrinkTime?: string | null
}

export default function GapForm({ isOpen, onClose, onSave, lastDrinkTime }: GapFormProps) {
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  // Reset form when opening
  useEffect(() => {
    if (isOpen) {
      if (lastDrinkTime) {
        setFromDate(formatDateTimeLocal(lastDrinkTime))
      } else {
        // Default to a week ago
        setFromDate(formatDateTimeLocal(moment().subtract(7, 'days').toDate()))
      }
      setToDate(formatDateTimeLocal(new Date()))
    }
  }, [isOpen, lastDrinkTime])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!fromDate || !toDate) {
      return
    }

    const entry: DrinkEntry = {
      started_at: formatDateForStorage(new Date(fromDate)),
      finished_at: formatDateForStorage(new Date(toDate)),
      is_gap: true,
      drink: { drink: '', milliliters: '', alcohol: '', calories: '' },
    }

    onSave(entry)
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Log Data Gap"
      size="sm"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" form="gap-form" className="btn btn-primary">
            Log Gap
          </button>
        </>
      }
    >
      <form id="gap-form" onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-[var(--text-secondary)]">
          Log a period when you weren't tracking drinks (e.g., vacation, technical issues).
          This helps keep your history accurate.
        </p>

        {/* From Date */}
        <div>
          <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
            From
          </label>
          <input
            type="datetime-local"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="w-full"
            required
          />
        </div>

        {/* To Date */}
        <div>
          <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
            To
          </label>
          <input
            type="datetime-local"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="w-full"
            required
          />
        </div>
      </form>
    </Modal>
  )
}
